import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ChatApi } from "./chat.api";
import { SnackbarService } from "../../core/services";
import * as ChatActions from "./chat.actions";
import * as ChannelActions from "../channel/channel.actions";
import { switchMap } from "rxjs/internal/operators/switchMap";
import { EMPTY, catchError, map, mergeMap, of } from "rxjs";

@Injectable()

export class ChatEffects {
    private readonly actions$ = inject(Actions);
    private readonly chatApi = inject(ChatApi);
    private readonly snackbar = inject(SnackbarService);

    private isCancelledError(error: unknown): boolean {
        if (!error) {
            return false;
        }

        if (typeof error === 'object' && (error as { cancelled?: boolean }).cancelled) {
            return true;
        }

        const message = this.toErrorMessage(error).toLowerCase();
        return message.includes('cancelled') || message.includes('canceled') || message.includes('context canceled');
    }

    private toErrorMessage(error: unknown): string {
        if (typeof error === 'string') {
            return error;
        }

        if (error && typeof error === 'object' && 'message' in error) {
            const maybeMessage = (error as { message?: unknown }).message;
            if (typeof maybeMessage === 'string') {
                return maybeMessage;
            }
        }

        try {
            return String(error);
        } catch {
            return 'Unknown error';
        }
    }

    getChatMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatGetRequest),
            mergeMap(({ payload }) => 
                this.chatApi.getChatMessages(payload).pipe(
                    map((response) => ChatActions.ChatGetSuccess({
                        channelId: payload.channelId,
                        mode: payload.mode ?? 'replace',
                        response: response.data,
                    })),
                    catchError((error) => {
                        if (this.isCancelledError(error)) {
                            return EMPTY;
                        }
                        return of(ChatActions.ChatGetFailure({ error: this.toErrorMessage(error) }));
                    })
                )
            )
        )
    );

    getChatMessagesFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatGetFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to fetch chat messages: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    getChatMessagesSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatGetSuccess),
            map(() => {
                
            })
        ),
        { dispatch: false }
    );

    deleteChatMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatDeleteRequest),
            switchMap(({ payload }) =>
                this.chatApi.deleteChatMessage(payload).pipe(
                    map((response) => ChatActions.ChatDeleteSuccess({
                        channelId: payload.channel_id,
                        messageId: payload.message_id,
                        message: response.message,
                    })),
                    catchError((error) => {
                        if (this.isCancelledError(error)) {
                            return EMPTY;
                        }
                        return of(ChatActions.ChatDeleteFailure({ error: this.toErrorMessage(error) }));
                    })
                )
            )
        )
    );

    deleteChatMessageSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatDeleteSuccess),
            map(() => ChannelActions.ChannelFetchRequest())
        )
    );

    deleteChatMessageSuccessNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatDeleteSuccess),
            map(({ message }) => {
                // this.snackbar.showNotification('success', '', message, 'Close');
            })
        ),
        { dispatch: false }
    );

    deleteChatMessageFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatDeleteFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to delete message: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    editChatMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatEditRequest),
            switchMap(({ payload }) =>
                this.chatApi.editChatMessage(payload).pipe(
                    map((response) => ChatActions.ChatEditSuccess({
                        channelId: payload.channel_id,
                        messageId: payload.message_id,
                        message: response.message,
                    })),
                    catchError((error) => {
                        if (this.isCancelledError(error)) {
                            return EMPTY;
                        }
                        return of(ChatActions.ChatEditFailure({ error: this.toErrorMessage(error) }));
                    })
                )
            )
        )
    );

    editChatMessageSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatEditSuccess),
            map(() => ChannelActions.ChannelFetchRequest())
        )
    );

    editChatMessageFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatEditFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to edit message: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    toggleChatReaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatReactionRequest),
            switchMap(({ payload }) =>
                this.chatApi.toggleChatReaction(payload).pipe(
                    map((response) => ChatActions.ChatReactionSuccess({
                        channelId: payload.channel_id,
                        messageId: payload.message_id,
                        message: response.message,
                    })),
                    catchError((error) => {
                        if (this.isCancelledError(error)) {
                            return EMPTY;
                        }
                        return of(ChatActions.ChatReactionFailure({ error: this.toErrorMessage(error) }));
                    })
                )
            )
        )
    );

    toggleChatReactionFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatReactionFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to update reaction: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    uploadAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatAttachmentUploadRequest),
            mergeMap(({ payload }) =>
                this.chatApi.uploadAttachment(payload).pipe(
                    map((response) => ChatActions.ChatAttachmentUploadSuccess({
                        channelId: payload.channel_id,
                        message: response.message,
                    })),
                    catchError((error) => {
                        if (this.isCancelledError(error)) {
                            return EMPTY;
                        }
                        return of(ChatActions.ChatAttachmentUploadFailure({ error: this.toErrorMessage(error) }));
                    })
                )
            )
        )
    );

    uploadAttachmentSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatAttachmentUploadSuccess),
            map(() => ChannelActions.ChannelFetchRequest())
        )
    );

    uploadAttachmentFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatActions.ChatAttachmentUploadFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to upload attachment: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );
}
    
